import { LocalDate, DateTimeFormatter } from "@js-joda/core";

import {
    declareAiCapability,
    provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// HHAX billing rejections describing its own filter bar. Its contract list is only the contracts that
// bill through HHAExchange, which is why it is read from the page rather than from every contract.
declareAiCapability({
    id: "hhaxBilling.filter",
    title: "Filter the HHAX billing rejections",
    effect: "prefill",
    description:
        "Sets the filters on HHAX Integration > Billing Rejections and re-runs its search: the date range, office, contract, and a free-text search over the table. The page opens on today. Only works while that page is open.",
    location: "the filter bar of the HHAX Integration > Billing Rejections page",
    scope: "page",
    permissionKey: "view_hhax_integration_section_billing",
    params: [
        { name: "from", type: "date", required: false, description: "First day to include, as yyyy-MM-dd." },
        { name: "to", type: "date", required: false, description: "Last day to include, as yyyy-MM-dd." },
        {
            name: "office",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getHhaxBillingOptions("offices"),
            description: "Limit to these offices."
        },
        {
            name: "contract",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getHhaxBillingOptions("contractTypes"),
            description: "Limit to these contracts. Only contracts billing through HHAExchange appear here."
        },
        {
            name: "search",
            type: "text",
            required: false,
            searchesFreeText: true,
            description: "Free text the table searches over every column."
        }
    ],
    examples: ["hhax rejections last week", "billing rejections for the europe office"]
});

let hhaxBillingScope = () => null;

function getHhaxBillingOptions(name) {
    return (hhaxBillingScope()?.[name] ?? []).map((option) => ({
        value: String(option.id),
        label: String(option.label ?? option.id)
    }));
}

//! @ngInject
export function hhaIntegrationBillingCtrl(
    $scope,
    $rootScope,
    $filter,
    NgTableParams,
    hhaxIntegrationService,
    DatabaseApi,
    toaster,
    selectionLogic,
    billingInvoicesProcessor
) {
   $scope.offices = DatabaseApi.offices().map((office) => ({
        id: office.id,
        label: office.name,
    }));
    DatabaseApi.contractTypes().filter(b => b.eBillingProvider === 'HHAExchange')

    $scope.contractTypes = DatabaseApi.contractTypes().
            filter(b => b.eBillingProvider === 'HHAExchange').
        map((contractType) => ({
            id: contractType.id,
            label: contractType.name,
        }));
    $scope.serviceCodesMap = new Map(DatabaseApi.activeServiceCodes().map(serviceCode => [serviceCode.id, serviceCode.code] || {}));
    $scope.officesMap = new Map($scope.offices.map(i => [i.id, i.label])) || {};
    $scope.contractTypesMap = new Map($scope.contractTypes.map(i => [i.id, i.label])) || {};
    $scope.hhaIntegrationGlobalFilter = { val: "" };
    $scope.selectedIssueName = "";
    $scope.selectedOffices = [];
    $scope.selectedContracts = [];

    $scope.integrationDatePicker = {
        from: new Date(LocalDate.now().format(DateTimeFormatter.ofPattern("MM/dd/yyyy"))),
        to: new Date(LocalDate.now().format(DateTimeFormatter.ofPattern("MM/dd/yyyy")))
    }

    $scope.tablesMap = {
        'Rejections': null,
        'Billing Exceptions': null,
        'Pre Adj Rejections': null
    }

    $scope.filterComponentOptions = {
        styleActive: true,
        scrollable: true,
        scrollableHeight: '300px',
        enableSearch: true
    };

    const initialize = () => {
        $scope.setTab("Rejections");
    }

    const initTable = () => {
        const options = {
            count: 25,
            sorting: { scheduledStartTime: "desc" }
        };

        $scope.tablesMap[$scope.activeTab] = new NgTableParams(options, {
            counts: [],
            dataset: $scope.billingRejections
        });
    }

    const reInitTable = (data) => {
        const options = {
            count: 25,
            sorting: { scheduledStartTime: "desc" }
        };

        $scope.tablesMap[$scope.activeTab] = new NgTableParams(options, {
            counts: [],
            dataset: data
        });
    }

    const initSelectionLogic = (items) => {
        $scope.selectionLogic = selectionLogic.createNewLogic(function (item) {
            $scope.selectionLogic.addItemToCollection(item);
        }, "visitInstanceId");

        if (items) {
            items.forEach((item) => {
                $scope.selectionLogic.initItem(item);
            });
        }
    }



    hhaxBillingScope = () => $scope;

    const releaseHhaxBillingFilter = provideAiCapability("hhaxBilling.filter", (args) => {
        const applied = [];
        const asList = (value) => (Array.isArray(value) ? value : [value]);
        const asDate = (text) => {
            const parts = String(text).split("-").map(Number);

            if (parts.length !== 3 || parts.some(isNaN)) {
                throw new Error("Could not read the date " + text + ".");
            }

            return new Date(parts[0], parts[1] - 1, parts[2]);
        };
        const pickAll = (available, requested, what) =>
            asList(requested).map((one) => {
                const found = (available ?? []).find(
                    (item) =>
                        String(item.id) === String(one) ||
                        String(item.label ?? "").toLowerCase() === String(one).toLowerCase()
                );

                if (found === undefined) {
                    throw new Error("This agency has no " + what + " " + one + ".");
                }

                return found;
            });

        if (args.from !== undefined) {
            $scope.integrationDatePicker.from = asDate(args.from);
        }

        if (args.to !== undefined) {
            $scope.integrationDatePicker.to = asDate(args.to);
        }

        if (args.from !== undefined || args.to !== undefined) {
            applied.push([args.from, args.to].filter(Boolean).join(" to "));
        }

        if (args.office !== undefined) {
            const offices = pickAll($scope.offices, args.office, "office");

            $scope.selectedOffices = offices.map((office) => ({ id: office.id }));
            applied.push(offices.map((office) => office.label).join(" and "));
        }

        if (args.contract !== undefined) {
            const contracts = pickAll($scope.contractTypes, args.contract, "HHAExchange contract");

            $scope.selectedContracts = contracts.map((contract) => ({ id: contract.id }));
            applied.push(contracts.map((contract) => contract.label).join(" and "));
        }

        if (args.search !== undefined) {
            $scope.hhaIntegrationGlobalFilter.val = String(args.search);
            applied.push('"' + String(args.search) + '"');
        }

        $scope.applySearch();

        return applied.length === 0
            ? "Re-ran the HHAX rejections search"
            : "Showing HHAX rejections: " + applied.join(", ");
    });

    $scope.$on("$destroy", releaseHhaxBillingFilter);

    $scope.applySearch = () => {
        initialize();
    }

   function getIntegrationBillingRejections() {
        $scope.isLoading = true;

        let getRejectionsFunctionByTab;

        if ($scope.activeTab === "Rejections") {
            getRejectionsFunctionByTab = hhaxIntegrationService.getResponseFileRejections($scope.integrationDatePicker, $scope.selectedOffices, $scope.selectedContracts);
        } else if ($scope.activeTab === "Billing Exceptions") {
            getRejectionsFunctionByTab = hhaxIntegrationService.getBillingExceptions($scope.integrationDatePicker, $scope.selectedOffices, $scope.selectedContracts);
        } else if ($scope.activeTab === "Pre Adj Rejections") {
            getRejectionsFunctionByTab = hhaxIntegrationService.getPreAdjRejections($scope.integrationDatePicker, $scope.selectedOffices, $scope.selectedContracts);
        }

        getRejectionsFunctionByTab.then(res => {
            $scope.billingRejections = res.rejections;
            initTable();
            initSelectionLogic($scope.billingRejections);
            initIssues();
            $scope.isLoading = false;
        }, function (err) {
            toaster.pop('error', 'Something went wrong', 'could not get DATA');
            $scope.isLoading = false;
        });
    }

    $scope.setTab = (tabName) => {
        $scope.isLoading = true;
        $scope.activeTab = tabName;
        getIntegrationBillingRejections();
    }

    $scope.getRowPatient = (row) => {
        return `${row.patientFirstName} ${row.patientLastName} ${row.patientDisplayId === null ? '' : `(${row.patientDisplayId})`}`;
    }

    $scope.getRowCaregiver = (row) => {
        if (row.caregiverId !== null) {
            return `${row.caregiverFirstName} ${row.caregiverLastName} ${row.caregiverDisplayId === null ? '' : `(${row.caregiverDisplayId})`}`;
        }
    }

    $scope.getRowServiceCode = (row) => {
        if (row.serviceCodeId !== undefined) {
            return `${$scope.serviceCodesMap.get(row.serviceCodeId)} (${row.serviceCodeId})`
        }
    }

    $scope.openPatientInNewTab = (patientId)  => {
        $rootScope.openPatientNewTab(patientId)
    }

    $scope.openPatientModal = (patientId)  => {
        $rootScope.openPatientModal(patientId)
    }

    $scope.openCaregiverInNewTab = (caregiverId)  => {
        $rootScope.openCaregiverNewTab(caregiverId)
    }

    $scope.toggleCalendarPopups = function (prop) {
        $scope.calendarPopups[prop] = !$scope.calendarPopups[prop];
    }

    $scope.onDateRangeChanged = (startDate, endDate) => {
        $scope.integrationDatePicker.from = startDate;
        $scope.integrationDatePicker.to = endDate;
    }

    $scope.applyHHAIntegrationGlobalSearch = () => {
        const filter = { $: $scope.hhaIntegrationGlobalFilter.val, issue: $scope.selectedIssueName };
        if ($scope.tablesMap[$scope.activeTab]) {
            angular.extend($scope.tablesMap[$scope.activeTab].filter(), filter);
        }
    };

    const initIssues = () => {
        $scope.issuesCounters = {};

        $scope.pageIssues = getIssuesWithCount($scope.billingRejections).map((item, index) => ({
            id: index,
            name: item,
            isSelected: false,
            tooltip: HHAXIssuesToolTip(item)
        }));
    }

    const getIssuesWithCount = (rejectionRecords) => {
        const uniqueIssues = new Set();

        for (const item of rejectionRecords) {
            const itemIssues = item.issue.split(",");

            for (const issue of itemIssues) {
                uniqueIssues.add(issue);
                
                if ($scope.issuesCounters[issue] === undefined) {
                    $scope.issuesCounters = {...$scope.issuesCounters, [issue]: 1}
                } else {
                    $scope.issuesCounters[issue]++;
                }
            }
        }

        return [...uniqueIssues].sort((a, b) => a.localeCompare(b));
    }

    $scope.getIssueCounterSuffix = (issue) => {
        if (!$scope.issuesCounters[issue]) {
          return "";
        }
        return ` (${$scope.issuesCounters[issue]})`;
    };

    const HHAXIssuesToolTip = (issue) => {
        let tooltip;

        switch(issue) {
            case "Medicaid Number is required.":
                tooltip = "Validate that the patient has a Medicaid Number on his profile page/active contract."
                break;  
            case "Caregiver Code is required.":
                tooltip = "Validate that there is a caregiver assigned to the visit.";
                break;
            case "Patient not found in HHAeXchange.":
                tooltip = "Validate that the Medicaid Number in HHAeXchange is equal to the one in Medflyt.";
                break;
            case "Procedure Code not found in HHAeXchange.":
                tooltip = "The service code 'export code' in Medflyt is different from the one HHAeXchange required. Please refer to HHAeXchange 'EDI Code Table Guide' of your state.";
                break;
            case "Overlapping shifts are not allowed.":
                tooltip = "This might be related to HHAeXchange account setup. Please get in touch with Medflyt's support team.";
                break;
            case "Agency is not linked with Payer.":
            case "Visit Edit Reason Code not found in HHAeXchange.":
            case "Visit Edit Action Taken not found in HHAeXchange.":
            case "Invalid value of Gender.":
            case "Payer ID is required.":
                tooltip = "Please get in touch with Medflyt's support team.";
                break;
            case "Procedure Code is required.":
                tooltip = "Validate that the service code's 'export code' in Medflyt is not empty.";
                break;
            case "Caregiver profile found with matching SSN and different Alt Caregiver Code.":
                tooltip = "Caregiver's Alt Code in HHAeXchange is not equal to Caregiver ID in Medflyt.";
                break;
            case "Visits cannot be imported prior to patient SOC date or after patient discharge date.":
                tooltip = "";
                break;
            case "Patient Diagnosis Code(DX Code) is required when visit is confirmed or billed.":
                tooltip = "Patient is missing dx code for these visits.";
                break;
            case "Visits that cross over midnight must be sent as two separate shifts.":
                tooltip = "Please get in touch with Medflyt's support team.";
                break;
            case "Caregiver Compliance":
                tooltip = "";
                break;
            case "No Authorization":
                tooltip = "";
                break;
            case "Shift Overlapping":
                tooltip = "";
                break;
            case "Timesheet Not Approved":
                tooltip = "";
                break;
            case "Caregiver Overlapping":
                tooltip = "";
                break;
            case "Incomplete Confirmation":
                tooltip = "";
                break;
            case "POC Compliance":
                tooltip = "";
                break;
            case "Pending billing of additional shifts on same day":
                tooltip = "";
                break;
            case "Missing Primary Diagnosis":
                tooltip = "";
                break;
            case "Billing greater than 24 hours":
                tooltip = "";
                break;
            case "Visits on same day/service code must be billed on same invoice":
                tooltip = "";
                break;
            default:
        }

        return tooltip;
        
    }

    $scope.filterByIssue = (issue, activeTab) => {
        let filter;
        
        if (issue.isSelected) {
            filter = { issue: "" };
            $scope.selectedIssueName = "";
            $scope.pageIssues = $scope.pageIssues.map(item => 
                issue.id === item.id ? ({...item, isSelected: false}) : item
            );
        } else {
            filter = { issue: issue.name };
            $scope.selectedIssueName = issue.name;
            $scope.pageIssues = $scope.pageIssues.map(item => 
                issue.id === item.id ? ({...item, isSelected: true}) : ({...item, isSelected: false})
            );
        }

        const filteredData = getIssueFilteredData(filter.issue);
        reInitTable(filteredData);
        $scope.selectionLogic.initItemsCollection(filteredData);
    }

    const getIssueFilteredData = (issue) => {
        let filteredData;

        $scope.billingRejections = $scope.billingRejections.map(element => ({
            ...element,
            isSelected: false
        }));
        filteredData = issue === "" ? $scope.billingRejections : $scope.billingRejections.filter(item => item.issue.includes(issue));

        return filteredData;
    }

    $scope.openModalToExportInvoices = function () {
        const selectedItems = $scope.selectionLogic.getSelectedItems();
        billingInvoicesProcessor.openInvoicesExporterModalByInvoiceVisits(selectedItems.map(item => item.invoiceVisitId), initialize());
    };

    $scope.downloadTable = () => {
        $scope.loadingCSV = true;
        const rows = [];
        const titles = ['Visit Instance ID', 'Visit Date', 'Invoice ID', 'Office', 'Contract', 'Service Code', 'Billed Amount', 'Patient Name', 'Patient Id'];
        if ($scope.activeTab === 'Rejections') {
            titles.push('Patient Medicaid Number');
            titles.push('Patient Member ID');
        }
        
        titles.push('Caregiver Name', 'Caregiver Id', 'Issue');

        if ($scope.activeTab === 'Rejections') {
            titles.push('Last Exported At');
        } else {
            titles.push('Last File Date');
        }

        rows.push(titles);

        $scope.selectionLogic.getSelectedItems().forEach(dataRow => {
            const csvRow = [];
            
            csvRow.push(`${dataRow.visitInstanceId}`);
            csvRow.push($filter("mfShortDate")(dataRow.scheduledStartTime) || '');
            csvRow.push(`${dataRow.invoiceId}`);
            csvRow.push(`${$scope.officesMap.get(dataRow.officeId) ?? ''}`);
            csvRow.push(`${$scope.contractTypesMap.get(dataRow.contractTypeId)}`);
            csvRow.push(`${$scope.serviceCodesMap.get(dataRow.serviceCodeId)}`);
            csvRow.push($filter("centsToDollars")(dataRow.billingAmount) || '');
            csvRow.push(`${dataRow.patientFirstName} ${dataRow.patientLastName}`);
            csvRow.push(`${dataRow.patientDisplayId ?? ''}`);
            if ($scope.activeTab === 'Rejections') {
                csvRow.push(`${dataRow.patientMedicaidNumber}`);
                csvRow.push(`${dataRow.patientMemberId}`);
            }
            csvRow.push(`${dataRow.caregiverFirstName ?? ''} ${dataRow.caregiverLastName ?? ''}`);
            csvRow.push(`${dataRow.caregiverDisplayId ?? ''}`);
            csvRow.push(`${dataRow.issue.replaceAll(",", " | ")}`);

            if ($scope.activeTab === 'Rejections') {
                csvRow.push($filter("mfShortDate")(dataRow.exportedAt) || '');
            } else {
                csvRow.push($filter("mfShortDate")(dataRow.hhaIntegrationBillingFileDatetime) || '');
            }

            rows.push(csvRow);
        });

        let csvContent = "data:text/csv;charset=utf-8,";
        rows.forEach(rowArray => {
            const row = rowArray.join(",");
            csvContent += row + "\r\n";
        });

        const encodedUri = encodeURI(csvContent);
        let link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `medflyt-export-HHAX-rejections-${$filter("date")(new Date(), "yyyy-MM-dd")}.csv`);
        document.body.appendChild(link);

        link.click();
        $scope.loadingCSV = false;
    };

    initialize();
};
